import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from "@tanstack/react-query"
import { useEffect } from "react"
import { pushRecentClient } from "@/utils/recentClients"
import {
  Badge,
  Box,
  Center,
  Container,
  Flex,
  Heading,
  Spinner,
  Tabs,
  Text,
} from "@chakra-ui/react"
import { FiArrowLeft } from "react-icons/fi"
import { ProviderClients } from "@/client"

import EditProviderClient from "@/components/ProviderClients/EditProviderClient"
import ClientBudgetsTab from "@/components/Client/ClientBudgetsTab"
import ClientAllowedServicesTab from "@/components/Client/ClientAllowedServicesTab"
import ClientCoContributionsTab from "@/components/Client/ClientCoContributionsTab"
import ClientOverviewTab from "@/components/Client/ClientOverviewTab"
import ClientSurveysTab from "@/components/Client/ClientSurveysTab"
import ClientServicesTab from "@/components/Client/ClientServicesTab"
import ClientPurchasesTab from "@/components/Client/ClientPurchasesTab"

interface ProviderClientsEditSearch {
  providerClientId: string
}

export const Route = createFileRoute("/_layout/provider/providerclients/edit")({
  component: ProviderClientsEdit,
  validateSearch: (search: Record<string, unknown>): ProviderClientsEditSearch => {
    return {
      providerClientId: (search.providerClientId as string) || '',
    }
  },
})

function ProviderClientsEdit() {
  const navigate = useNavigate()
  const { providerClientId } = Route.useSearch()

  const { data: providerClient, isLoading, error } = useQuery({
    queryKey: ["providerClient", providerClientId],
    queryFn: () => ProviderClients.readProviderClientById({ path: { id: providerClientId } }),
    enabled: !!providerClientId,
  })

  const displayName = [
    providerClient?.prefername || providerClient?.firstname,
    providerClient?.lastname,
  ].filter(Boolean).join(" ") || "Unknown Client"

  // Must be before any early returns — hooks must always run in the same order
  useEffect(() => {
    if (!providerClient) return
    if (!sessionStorage.getItem("selectedProviderId")) {
      sessionStorage.setItem("selectedProviderId", providerClient.providerId)
    }
    pushRecentClient({
      id: providerClient.id,
      displayName,
      providerClientId: providerClient.providerClientId,
    })
  }, [providerClient?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!providerClientId) {
    return (
      <Container maxW="full" pt={12}>
        <Text color="red.500">No provider client ID provided.</Text>
      </Container>
    )
  }

  if (isLoading) {
    return (
      <Container maxW="full" pt={12}>
        <Center mt={16}><Spinner size="lg" /></Center>
      </Container>
    )
  }

  if (error || !providerClient) {
    return (
      <Container maxW="full" pt={12}>
        <Text color="red.500">Failed to load provider client.</Text>
      </Container>
    )
  }

  return (
    <Container maxW="full" pt={8} pb={12}>
      {/* Back link */}
      <button
        onClick={() => navigate({ to: `/provider/providerclients/${providerClient.providerId}` as never })}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem", color: "#718096", marginBottom: 16, background: "none", border: "none", cursor: "pointer", padding: 0 }}
      >
        <FiArrowLeft size={13} /> Provider Clients
      </button>

      {/* Client identity header */}
      <Flex align="baseline" gap={3} mb={6}>
        <Heading size="xl">{displayName}</Heading>
        <Badge colorPalette="gray" size="lg" fontFamily="mono">
          {providerClient.providerClientId}
        </Badge>
        {providerClient.pensionType && (
          <Badge colorPalette="blue" size="sm">{providerClient.pensionType}</Badge>
        )}
      </Flex>

      {/* Tabs */}
      <Tabs.Root lazyMount unmountOnExit defaultValue="overview">
        <Tabs.List>
          <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
          <Tabs.Trigger value="personal">Personal</Tabs.Trigger>
          <Tabs.Trigger value="budgets">Budgets</Tabs.Trigger>
          <Tabs.Trigger value="allowed-services">Allowed Services</Tabs.Trigger>
          <Tabs.Trigger value="co-contributions">Co-Contributions</Tabs.Trigger>
          <Tabs.Trigger value="surveys">Surveys</Tabs.Trigger>
          <Tabs.Trigger value="services">Services</Tabs.Trigger>
          <Tabs.Trigger value="purchases">Purchases</Tabs.Trigger>
        </Tabs.List>

        <Box pt={5}>
          <Tabs.Content value="overview">
            <ClientOverviewTab client={providerClient} providerClientId={providerClientId} />
          </Tabs.Content>
          <Tabs.Content value="personal">
            <EditProviderClient
              providerClient={providerClient}
              onBack={() => navigate({ to: `/provider/providerclients/${providerClient.providerId}` as never })}
            />
          </Tabs.Content>
          <Tabs.Content value="budgets">
            <ClientBudgetsTab providerClientId={providerClientId} />
          </Tabs.Content>
          <Tabs.Content value="allowed-services">
            <ClientAllowedServicesTab
              providerClientId={providerClientId}
              providerId={providerClient.providerId}
              currentClientTypeId={providerClient.providerClientTypeId}
            />
          </Tabs.Content>
          <Tabs.Content value="co-contributions">
            <ClientCoContributionsTab
              providerClientId={providerClientId}
              pensionType={providerClient.pensionType}
            />
          </Tabs.Content>
          <Tabs.Content value="surveys">
            <ClientSurveysTab providerClientId={providerClientId} />
          </Tabs.Content>
          <Tabs.Content value="services">
            <ClientServicesTab
              providerClientId={providerClientId}
              providerId={providerClient.providerId}
              pensionType={providerClient.pensionType}
            />
          </Tabs.Content>
          <Tabs.Content value="purchases">
            <ClientPurchasesTab
              providerClientId={providerClientId}
              providerId={providerClient.providerId}
            />
          </Tabs.Content>
        </Box>
      </Tabs.Root>
    </Container>
  )
}
